/**
 * Catching a residential address the customer didn't declare.
 *
 * Residential pickups and deliveries cost more, so before the screen accepts
 * an address that isn't marked residential we ask the API whether it looks
 * like one, and if so ask the customer. Saying yes invalidates the quote they
 * chose, so the shipment goes back to the details screen to be re-quoted.
 *
 * Clearing the quote is also why the notice dialog exists: the screen's
 * no-quote redirect would otherwise unmount the dialog before it is seen. The
 * ref this returns is what holds that redirect off.
 */

import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { useShipmentService } from "@/Services/ShipmentService";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BookShipmentConfirmResidentialDialog } from "../Dialogs/BookShipmentConfirmResidentialDialog";
import {
  loadFromShipmentToModify,
  setDeliveryLocation,
  setPickupLocation,
} from "@/Slices/bookShipmentSlice";
import { BookShipmentConfirmResidentialResultType } from "../Types/BookShipmentConfirmResidentialResultType";

interface BookShipmentResidentialCheckInput {
  shipment: PreBookingShipment;
  savedLocations: Array<SavedLocation>;
  context: LocationContext;
  location: PreQuoteLocation;
}

export function useBookShipmentResidentialCheck(
  input: BookShipmentResidentialCheckInput,
) {
  const { shipment, savedLocations, context, location } = input;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const shipmentsApi = useShipmentsApi();
  const shipmentsService = useShipmentService();

  // Adding residential deliberately clears the selected quote (the shipment
  // is being modified for new quotes), so the no-quote redirect must stay
  // quiet during that flow — the "Details Updated" notice dialog owns the
  // navigation to /book/details instead.
  const addingResidentialRef = useRef(false);

  // The residential prompt resolves the promise the Next handler awaits,
  // so the two dialog buttons resume the same flow the legacy imperative
  // modal did.
  const [residentialPromptOpen, setResidentialPromptOpen] = useState(false);
  const residentialResolverRef = useRef<
    ((result: BookShipmentConfirmResidentialResultType) => void) | undefined
  >(undefined);
  const [residentialNoticeOpen, setResidentialNoticeOpen] = useState(false);
  // Inside pickup/delivery isn't available for residential, so becoming
  // residential silently drops it. The notice says so — otherwise the service
  // they asked for just isn't on the re-quote and they find out from the price.
  const [insideRemoved, setInsideRemoved] = useState(false);

  async function checkIfAddressIsResidential(): Promise<boolean> {
    try {
      const isResidentialResult =
        await shipmentsApi.checkIfAddressIsResidential({
          checkIfAddressIsResidentialRequest: {
            // isScreenCompletelyEntered should have done sufficient verifications to make this cast safe
            address: location.address as Address,
          },
        });

      return isResidentialResult.isResidential;
    } catch {
      // If there is a network error - just assume it's false
      return false;
    }
  }

  /** Returns whether the screen may continue to submitting the address. */
  async function confirmNotResidential(): Promise<boolean> {
    if (location.locationType === LocationType.Residential) {
      return true;
    }

    const isResidential = await checkIfAddressIsResidential();
    if (!isResidential) {
      return true;
    }

    const residentialConfirmResult =
      await new Promise<BookShipmentConfirmResidentialResultType>((resolve) => {
        residentialResolverRef.current = resolve;
        setResidentialPromptOpen(true);
      });

    if (
      residentialConfirmResult !==
      BookShipmentConfirmResidentialResultType.AddResidentialToQuote
    ) {
      return true;
    }

    // Set before the dispatch below clears the selected quote, so the
    // no-quote redirect effect doesn't fire and unmount this screen
    // before the notice dialog opens.
    addingResidentialRef.current = true;
    setInsideRemoved(
      (context === LocationContext.Pickup
        ? shipment.pickupLocation
        : shipment.deliveryLocation
      ).accessorials.includes("LOGISTICS_INSIDE"),
    );
    dispatch(loadFromShipmentToModify({ shipment, savedLocations }));
    if (context === LocationContext.Pickup) {
      const updatedPickupLocation: AddLocationLocationType = {
        ...shipment.pickupLocation,
        locationType: LocationType.Residential,
      };
      dispatch(setPickupLocation(updatedPickupLocation));
    }
    if (context === LocationContext.Delivery) {
      const updatedDeliveryLocation: AddLocationLocationType = {
        ...shipment.deliveryLocation,
        locationType: LocationType.Residential,
      };
      dispatch(setDeliveryLocation(updatedDeliveryLocation));
    }

    await shipmentsService.deleteQuotedShipment(
      shipment.shipmentId,
      "Modified to add residential",
    );

    // The notice navigates to /book/details when dismissed — unlike the
    // legacy global alert, a local dialog wouldn't survive navigating
    // away first.
    setResidentialNoticeOpen(true);
    return false;
  }

  // Declared so the screen can render these above its early returns: the
  // add-residential flow clears the selected quote while the notice dialog is
  // open, and an early return without them would silently unmount it (a
  // screen that owns an open Dialog must never unmount it).
  const dialogs: ReactNode = (
    <>
      <BookShipmentConfirmResidentialDialog
        open={residentialPromptOpen}
        onOpenChange={setResidentialPromptOpen}
        onDone={(result) => {
          setResidentialPromptOpen(false);
          residentialResolverRef.current?.(result);
          residentialResolverRef.current = undefined;
        }}
      />
      <Dialog
        open={residentialNoticeOpen}
        onOpenChange={(open) => {
          if (!open) {
            setResidentialNoticeOpen(false);
            navigate("/book/details");
          }
        }}
        title="Details Updated"
        footer={
          <Button
            primary
            onClick={() => {
              setResidentialNoticeOpen(false);
              navigate("/book/details");
            }}
          >
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {insideRemoved
            ? `Residential has now been added. Inside ${context === LocationContext.Pickup ? "pickup" : "delivery"} has been removed, as it is not available for residential locations. Please confirm and re-request quotes.`
            : "Residential has now been added. Please confirm and re-request quotes."}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return { addingResidentialRef, confirmNotResidential, dialogs };
}
